import { FilterExpression, IdPathParameters, like, Pageable, SearchOperation, Sort } from '@infuse/api/common';
import { User } from '@infuse/api/iam/user';
import { ColumnSort, updateSort } from '@infuse/ui/domain';
import { useInfiniteQuery } from '@tanstack/vue-query';
import { computed, ref } from 'vue';

const createSearchFilter = (searchQuery: string): FilterExpression | undefined => {
  if (searchQuery === '') {
    return;
  }

  const filterOptions = searchQuery
    .toLowerCase()
    .split(' ')
    .filter((part: string): boolean => part !== undefined && part !== null && part !== '')
    .map((part: string): string => `%${part}%`)
    .map((part: string): FilterExpression => {
      return new FilterExpression().and(like('lower(email)', part).or(like('lower(name)', part)));
    });

  if (filterOptions.length === 0) {
    return;
  }
  return filterOptions.reduce((acc, cv) => acc.and(cv));
};

export function useGenericUsers({
  apiFn,
  queryKeys,
  pageSize = 20,
  onlyEnableWithParentId = false,
  initialParentId
}: {
  apiFn: SearchOperation<User> | SearchOperation<User, IdPathParameters<string>>;
  queryKeys: string[];
  pageSize?: number;
  onlyEnableWithParentId?: boolean;
  initialParentId?: string;
}) {
  const searchQuery = ref<string | undefined>();
  const fetchParentId = ref<string | undefined>(initialParentId);
  const sort = ref<Sort[]>([]);

  const setSort = (columnSort: ColumnSort) => {
    const newSort = [...sort.value];
    updateSort(newSort, columnSort);
    sort.value = newSort;
  };

  const query = useInfiniteQuery<Pageable<User>>({
    initialPageParam: 0,
    queryKey: [...queryKeys, fetchParentId, searchQuery, sort],
    enabled: () => (onlyEnableWithParentId ? !!fetchParentId.value : true),
    queryFn: async ({ pageParam }) => {
      return apiFn(
        {
          size: pageSize,
          page: Number(pageParam),
          filter: searchQuery.value ? createSearchFilter(searchQuery.value) : undefined,
          sort: sort.value.length ? sort.value : [{ key: 'email' }]
        },
        undefined,
        fetchParentId.value ? { id: fetchParentId.value } : undefined
      );
    },
    getNextPageParam: (lastPage) => (lastPage.last ? undefined : lastPage.pageable.pageNumber + 1)
  });

  const users = computed(() => query.data.value?.pages.flatMap((page) => page.content) || []);
  const currentPage = computed(() => (query.data.value ? (query.data.value.pageParams.slice(-1)[0] as number) : 0));
  const totalElements = computed(() => query.data.value?.pages[0]?.totalElements ?? 0);

  return {
    ...query,
    searchQuery,
    fetchParentId,
    currentPage,
    users,
    totalElements,
    setSort
  };
}
